const { getTheme, log } = require("./commons");

// Guard against client-side execution — server-only modules are conditionally required
const isServer = typeof window === "undefined";
const fetch = isServer ? require("@adobe/node-fetch-retry") : null;
const getConfig = isServer ? require("../../config").getConfig : null;
// IS_DEV drives the token source: shell service in dev, S3 in e2e/prod
const IS_DEV = isServer ? require("../../config").IS_DEV : false;
// Cache TTL: tokens are refreshed once per day to avoid unnecessary S3/shell service calls
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day cache

// Initialize the server-side in-memory cache for IDS tokens on first module load
if (isServer && !global.__idsTokensCache) {
  global.__idsTokensCache = {
    tokensUrlMap: null,
    timestamp: null
  };
}

/**
 * Checks whether the in-memory IDS tokens cache is still valid.
 * An empty tokensUrlMap is treated as invalid to force a re-fetch.
 * @returns {boolean}
 */
function isCacheValid() {
  const cache = global.__idsTokensCache;
  if (!cache?.tokensUrlMap || !cache?.timestamp || Object.keys(cache.tokensUrlMap).length === 0) {
    return false;
  }
  return Date.now() - cache.timestamp < CACHE_TTL_MS;
}

/**
 * Fetches IDS design token URLs from the Intuit shell service.
 * Used in local dev environments to avoid requiring S3 IAM role credentials.
 * Requires SHELL_SERVICE_ENDPOINT config and shellServiceApiKey from IDPS secrets.
 * @returns {Promise<Object|null>} Map of theme name → CSS URL, or null on failure
 */
async function fetchIdsTokensFromShellService() {
  if (!isServer) {
    return null;
  }

  try {
    const apiKey = global.idpsSecrets?.["keys/raas/shellServiceApiKey"];
    const endpoint = getConfig("SHELL_SERVICE_ENDPOINT");

    if (!apiKey || !endpoint) {
      log.warn(
        "EVENT_NAME=SHELL_SERVICE_CONFIG_MISSING; Missing shell service API key or endpoint"
      );
      return null;
    }

    log.info(`EVENT_NAME=IDS_TOKENS_FETCH_INITIATED; Fetching from shell service ${endpoint}`);

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Intuit_APIKey intuit_apikey=${apiKey}, intuit_apkey_version=1.0`
      },
      retryOptions: {
        retryMaxDuration: 5000,
        retryInitialDelay: 500
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const tokensMap = data?.shellExperiments?.idsTokensVersions;

    if (tokensMap && Object.keys(tokensMap).length > 0) {
      global.__idsTokensCache.tokensUrlMap = tokensMap;
      global.__idsTokensCache.timestamp = Date.now();
      log.info(
        `EVENT_NAME=IDS_TOKENS_FETCH_SUCCESS; Fetched ${Object.keys(tokensMap).length} themes from shell service`
      );
      return tokensMap;
    }

    throw new Error("Invalid or empty tokens map from shell service");
  } catch (error) {
    log.error(
      `EVENT_NAME=IDS_TOKENS_FETCH_ERROR; Error fetching from shell service: ${error.message}`
    );
    return null;
  }
}

/**
 * Fetches IDS design token URLs from S3.
 * Used in e2e and production environments.
 * Requires IDS_TOKENS_S3_BUCKET and IDS_TOKENS_S3_KEY config values.
 * @returns {Promise<Object|null>} Map of theme name → CSS URL, or null on failure
 */
async function fetchIdsTokensFromS3() {
  if (!isServer) {
    return null;
  }

  try {
    const s3Bucket = getConfig("IDS_TOKENS_S3_BUCKET");
    const s3Key = getConfig("IDS_TOKENS_S3_KEY");

    if (!s3Bucket || !s3Key) {
      log.warn(
        "EVENT_NAME=IDS_TOKENS_S3_CONFIG_MISSING; Missing S3 bucket or key config for IDS tokens"
      );
      return null;
    }

    log.info(
      `EVENT_NAME=IDS_TOKENS_FETCH_INITIATED; Fetching from s3://${s3Bucket}/${s3Key}`
    );

    const { getS3Client } = require("../../services/aws/client");
    const s3 = await getS3Client();
    const response = await s3
      .getObject({ Bucket: s3Bucket, Key: s3Key })
      .promise();
    const tokensMap = JSON.parse(response.Body.toString("utf8"));

    if (tokensMap && Object.keys(tokensMap).length > 0) {
      global.__idsTokensCache.tokensUrlMap = tokensMap;
      global.__idsTokensCache.timestamp = Date.now();
      log.info(
        `EVENT_NAME=IDS_TOKENS_FETCH_SUCCESS; Fetched ${
          Object.keys(tokensMap).length
        } themes from S3`
      );
      return tokensMap;
    }

    throw new Error("IDS tokens map from S3 is empty");
  } catch (error) {
    log.error(
      `EVENT_NAME=IDS_TOKENS_FETCH_ERROR; Error fetching from S3: ${error.message}`
    );
    return null;
  }
}

/**
 * Initializes the IDS tokens URL map on server startup.
 * - In dev: fetches from the Intuit shell service (no S3 IAM role needed)
 * - In e2e/prod: fetches from S3 (gutenberg-static bucket)
 * Returns early if the cache is still valid to avoid redundant fetches.
 * Called once at server startup and refreshed via setInterval in server.js.
 * @returns {Promise<Object>} Map of theme name → CSS URL
 */
async function initializeTokensUrlMap() {
  if (!isServer) {
    return {};
  }

  if (isCacheValid()) {
    return global.__idsTokensCache.tokensUrlMap;
  }

  // Route to shell service in dev, S3 in e2e/prod
  const tokens = IS_DEV
    ? await fetchIdsTokensFromShellService()
    : await fetchIdsTokensFromS3();

  if (tokens) {
    return tokens;
  }

  log.error(
    `EVENT_NAME=IDS_TOKENS_UNAVAILABLE; Failed to load IDS tokens from ${IS_DEV ? "shell service" : "S3"}`
  );
  return {};
}

/**
 * Returns the IDS tokens URL map for client-side consumption.
 * On the client, reads from window.__raasInternal injected by _document.js.
 * On the server, reads from the in-memory global cache populated at startup.
 * @returns {Object} Map of theme name → CSS URL
 */
function getTokensUrlMap() {
  if (
    typeof window !== "undefined" &&
    window.__raasInternal?.idsTokensVersions
  ) {
    return window.__raasInternal.idsTokensVersions;
  }
  return global.__idsTokensCache?.tokensUrlMap || {};
}

/**
 * Checks whether a theme's CSS is already mounted in the DOM.
 * Used to filter out already-loaded themes from tokensUrlMap to avoid duplicate loads.
 * ThemeOrchestrationProvider injects a <style> element with id="${theme}-tokens".
 * @param {string} theme
 * @returns {boolean}
 */
function isThemeMounted(theme) {
  if (typeof document === "undefined") {
    return false;
  }
  return !!document.getElementById(`${theme}-tokens`);
}

/**
 * Returns the fallback theme CSS URL from page metadata.
 * Used as the fallbackUrl prop for ThemeOrchestrationProvider so it has
 * a URL to load if the primary theme resolution fails.
 * @param {Object} pageProps
 * @returns {string} CSS URL or empty string
 */
function getFallbackThemeUrl(pageProps) {
  return pageProps?.metaData?.global_design_style_token_sheet || "";
}

/**
 * Builds the window.__raasInternal config object injected into the SSR HTML by _document.js.
 * This makes server-side state (token URLs, BU, multi-theme flag) available to client-side code
 * without an additional API call.
 * @param {boolean} multiThemeEnabled - whether multi-theme is enabled for this site (from site config)
 * @returns {Object}
 */
function getRaasInternalConfig(multiThemeEnabled = false) {
  return {
    idsTokensVersions: global.__idsTokensCache?.tokensUrlMap || {},
    multiThemeEnabled: multiThemeEnabled
  };
}

/**
 * Returns the active theme name for the app.
 * - On client: reads from document.body[data-theme] set during SSR
 * - On server: resolves via getTheme() using the sitename from pageProps
 * @param {Object} pageProps
 * @returns {string} theme name
 */
function getAppTheme(pageProps) {
  const fallbackTheme = "intuit";

  if (typeof document !== "undefined") {
    // Client-side: data-theme is set on body by the server during SSR
    const bodyTheme = document.body?.getAttribute("data-theme");
    return bodyTheme || fallbackTheme;
  } else if (pageProps?.metaData?.sitename) {
    try {
      const renderingMetadata = { siteName: pageProps.metaData.sitename };
      const calculatedTheme = getTheme(renderingMetadata);
      return calculatedTheme;
    } catch (error) {
      log.error(`Server getTheme failed: ${error.message}`);
      return fallbackTheme;
    }
  }
  return fallbackTheme;
}

/**
 * Returns the active color scheme for the app.
 * - On client: reads from document.body[data-color-scheme] set during SSR
 * - On server: reads from pageProps.metaData.colorScheme
 * @param {Object} pageProps
 * @returns {string} "light" | "dark"
 */
function getAppColorScheme(pageProps) {
  const fallbackColorScheme = "light";

  if (typeof document !== "undefined") {
    // Client-side: data-color-scheme is set on body by the server during SSR
    const bodyColorScheme = document.body?.getAttribute("data-color-scheme");
    return bodyColorScheme || fallbackColorScheme;
  }

  return pageProps?.metaData?.colorScheme || fallbackColorScheme;
}

module.exports = {
  getAppTheme,
  getAppColorScheme,
  getTokensUrlMap,
  isThemeMounted,
  getFallbackThemeUrl,
  getRaasInternalConfig,
  initializeTokensUrlMap,
  fetchIdsTokensFromShellService,
  fetchIdsTokensFromS3,
  CACHE_TTL_MS
};
